import {
  GameStatuses,
  type RelatedScheduleResponse,
  type ScoreboardResponse,
} from '@on3/api';
import {
  type GameTeamData,
  getGameTime,
  getPredictionOutcome,
  getTeamData,
} from '@on3/api/utils/games';
import { Check, XMark } from '@on3/ui-lib/components/Svg';
import { adjustColorForReadability } from '@on3/ui-lib/utils/accessibility/color';
import { handleKeyDown } from '@on3/ui-lib/utils/accessibility/keyDown';
import { getAssetUrl } from '@on3/ui-lib/utils/images';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { On3Avatar } from '@public/components/Image/On3Avatar';
import { type GameActions, GameWrapper } from '@public/hocs/Game/GameWrapper';
import clsx from 'clsx';
import { format, parse } from 'date-fns';
import Link from 'next/link';
import { useCallback } from 'react';

import styles from './ScheduleItem.module.scss';

interface IScheduleItemProps {
  game: ScoreboardResponse;
  relatedModel?: RelatedScheduleResponse;
}

const decimalToPercentage = (decimal: number | null | undefined) => {
  if (!decimal) return '-';

  return `${Math.round(decimal * 100)}%`;
};

export const ScheduleItem = ({ game, relatedModel }: IScheduleItemProps) => {
  const {
    currentOrgIsHome,
    currentOrgIsWinner,
    startDate,
    statusEnum,
    winner,
  } = game || {};
  const currentYear = new Date().getFullYear();
  const parsedDate = game?.startDateUtc
    ? new Date(game.startDateUtc * 1000)
    : startDate && startDate !== 'TBD' && parse(startDate, 'M/d', new Date());

  const gameDate = parsedDate && format(parsedDate, 'EEE M/d');
  const gameTime = getGameTime(game);

  const showScores = statusEnum !== GameStatuses.Scheduled;
  const predictionOutcome = getPredictionOutcome(game);

  const primaryColor = adjustColorForReadability(
    relatedModel?.organization?.primaryColor ?? '#fe3b1f',
  );
  const sport = slugify(relatedModel?.sport?.name || 'football');
  const year = relatedModel?.year || currentYear;
  const homeTeam = getTeamData(true, game);
  const awayTeam = getTeamData(false, game);
  const currentTeam = currentOrgIsHome ? homeTeam : awayTeam;

  const getScoreStyle = (team: { key?: number }) => {
    if (team.key !== currentTeam.key || statusEnum !== GameStatuses.Completed)
      return;

    return currentOrgIsWinner ? styles.colorWin : styles.colorLoss;
  };

  const showRank = (ranking?: number | null) => {
    if (!ranking || ranking > 50) return false;

    return true;
  };

  const getTeamScheduleUrl = useCallback(
    (slug: GameTeamData['urlSlug']) => {
      if (year !== currentYear) {
        return `/high-school/${slug}/${sport}/${year}/schedule/`;
      }

      return `/high-school/${slug}/${sport}/schedule/`;
    },
    [currentYear, sport, year],
  );

  return (
    <GameWrapper
      game={{
        ...game,
        currentTeam: {
          key: currentTeam.key as number,
          name: currentTeam.name as string,
          primaryColor,
        },
      }}
    >
      {(gameActions: GameActions) => (
        <tr
          className={styles.block}
          data-status={statusEnum}
          data-winner={winner?.key === homeTeam.key ? 'home' : 'away'}
        >
          <td headers="date">
            <div
              className={clsx(styles.date, {
                [styles.final]: statusEnum === GameStatuses.Completed,
                [styles.inProgress]: statusEnum === GameStatuses.InProgress,
                [styles.delayed]: statusEnum === GameStatuses.Delayed,
              })}
              onClick={gameActions.edit}
              onKeyDown={(e) => handleKeyDown(e)}
              role={gameActions.edit && 'button'}
              tabIndex={gameActions.edit && 0}
            >
              <span>{gameDate}</span>
              <span className={styles.smallText}>{gameTime}</span>
            </div>
          </td>
          <td headers="opponent">
            <div>
              <div className={styles.away}>
                <span className={styles.ranking}>
                  {showRank(awayTeam.ranking.state) &&
                    `#${awayTeam.ranking.state}`}
                </span>
                <Link
                  data-away="name"
                  href={getTeamScheduleUrl(awayTeam.urlSlug)}
                >
                  <figure>
                    <On3Avatar
                      altText={awayTeam?.name}
                      height={25}
                      source={getAssetUrl(awayTeam?.avatar)}
                      teamAvatar
                      width={25}
                    />
                  </figure>
                  <span>{awayTeam?.name}</span>
                </Link>
                <span className={styles.smallText}>
                  ({awayTeam.record || '0-0'})
                </span>
              </div>
              <div className={styles.home}>
                <span className={styles.ranking}>
                  {showRank(homeTeam.ranking.state) &&
                    `#${homeTeam.ranking.state}`}
                </span>
                <Link
                  data-home="name"
                  href={getTeamScheduleUrl(homeTeam.urlSlug)}
                >
                  <figure>
                    <On3Avatar
                      altText={homeTeam?.name}
                      height={25}
                      source={getAssetUrl(homeTeam?.avatar)}
                      teamAvatar
                      width={25}
                    />
                  </figure>
                  <span data-home="name">{homeTeam?.name}</span>
                </Link>
                <span className={styles.smallText}>
                  ({homeTeam.record || '0-0'})
                </span>
              </div>
            </div>
          </td>
          <td headers="score">
            <div
              onClick={gameActions.edit}
              onKeyDown={(e) => handleKeyDown(e)}
              role={gameActions.edit && 'button'}
              tabIndex={gameActions.edit && 0}
            >
              <span className={getScoreStyle(awayTeam)} data-away="score">
                {showScores ? awayTeam.score : '-'}
              </span>
              <span className={getScoreStyle(homeTeam)} data-home="score">
                {showScores ? homeTeam.score : '-'}
              </span>
            </div>
          </td>
          <td headers="prediction">
            <div className={styles.predictions}>
              <div>
                <span data-away="prediction">{awayTeam.prediction ?? '-'}</span>
                <span
                  data-away="probability"
                  data-content={decimalToPercentage(awayTeam.winProbability)}
                />
              </div>
              <div>
                <span data-home="prediction">{homeTeam.prediction ?? '-'}</span>
                <span
                  data-content={decimalToPercentage(homeTeam.winProbability)}
                  data-home="probability"
                />
              </div>
            </div>
          </td>
          <td headers="winProbability">
            {statusEnum === GameStatuses.Completed ? (
              <div
                className={clsx(styles.result, {
                  [styles.correct]: predictionOutcome,
                  [styles.incorrect]: !predictionOutcome,
                })}
              >
                {predictionOutcome ? <Check /> : <XMark />}
              </div>
            ) : (
              <div>
                <span data-away="winProbability">
                  {decimalToPercentage(awayTeam.winProbability)}
                </span>
                <span data-home="winProbability">
                  {decimalToPercentage(homeTeam.winProbability)}
                </span>
              </div>
            )}
          </td>
        </tr>
      )}
    </GameWrapper>
  );
};
